import { FunctionComponent, useState } from 'react';
import { Card, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { createPortal } from 'react-dom';
import Moment from 'react-moment';
import l from '../../../../lang';
import ChipStatus from '../../../common/chip-status';
import { Button } from '../../../common/button';
import { ReactComponent as FileDownload } from '../../../../assets/icons/file-download.svg';
import { Urls } from '../../../../config/urls';
import { createDate, getMappedCountryCode } from '../../../../utils/index';
import { Payout } from '../../../../store/payouts/interfaces';
import { usePayouts } from '../../../../store/payouts';
import './styles.scss';

interface PayoutDrawerProps {
  onClose: () => void;
  data: Payout;
}

const PayoutDrawer: FunctionComponent<PayoutDrawerProps> = ({ onClose, data }) => {
  const [paymentDetailOpen, setPaymentDetailOpen] = useState(true);
  const [recipientDetailOpen, setRecipientDetailOpen] = useState(true);
  const { exportPayoutDetail } = usePayouts();

  return createPortal(
    <div className="payout_drawer" onClick={onClose}>
      <Card className="payout_drawer__content" onClick={ev => ev.stopPropagation()}>
        <div className="payout_drawer__content__header">
          <div>{l('payouts.drawer.title')}</div>
          <IconButton className="payout_drawer__content__close" onClick={onClose}>
            <CloseIcon color="action" />
          </IconButton>
        </div>
        <div className="payout_drawer__content__body">
          <div className="payout_drawer__summary">
            <div className="payout_drawer__summary__row">
              <span className="payout_drawer__summary__label">{l('payouts.drawer.payoutId')}</span>
              <span className="payout_drawer__summary__value">{data.payout_id}</span>
            </div>
            <div className="payout_drawer__summary__row">
              <span className="payout_drawer__summary__label">{l('payouts.drawer.creationDate')}</span>
              <span className="payout_drawer__summary__value">
                <Moment format="DD/MM/YYYY">{createDate(data.created_at)}</Moment>
              </span>
            </div>
            <div className="payout_drawer__summary__row">
              <span className="payout_drawer__summary__label">{l('payouts.drawer.estimatedDate')}</span>
              <span className="payout_drawer__summary__value">
                {data.completed_at ? <Moment format="DD/MM/YYYY">{createDate(data.completed_at)}</Moment> : '-'}
              </span>
            </div>
            <div className="payout_drawer__summary__row">
              <span className="payout_drawer__summary__label">{l('payouts.status')}</span>
              <span className="payout_drawer__summary__value">
                <ChipStatus status={data.status} text={l(`transactions.status.${data.status}`)} />
              </span>
            </div>
            <div className="payout_drawer__summary__row">
              <span className="payout_drawer__summary__label">{l('payouts.country')}</span>
              <span className="payout_drawer__summary__value payout_drawer__country">
                <img
                  alt=""
                  className="payout_drawer__country__flag country-flag--rounded"
                  src={`${Urls.roundedFlags.replace(':code', getMappedCountryCode(data.country))}`}
                />
                &nbsp;{data.country}
              </span>
            </div>
          </div>

          <div className="payout_drawer__section">
            <div
              className="payout_drawer__section__header"
              onClick={() => setPaymentDetailOpen(!paymentDetailOpen)}
            >
              <span>{l('payouts.drawer.paymentDetail')}</span>
              <KeyboardArrowUpIcon
                className={`payout_drawer__section__arrow ${!paymentDetailOpen ? 'payout_drawer__section__arrow--collapsed' : ''}`}
              />
            </div>
            {paymentDetailOpen && (
              <div className="payout_drawer__section__body">
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.amountSent')}</span>
                  <span className="payout_drawer__summary__value">
                    {data.currency_to_pay} {data.amount}
                  </span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.currency')}</span>
                  <span className="payout_drawer__summary__value">{data.currency_to_pay}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.fee')}</span>
                  <span className="payout_drawer__summary__value">
                    {data.fee_currency} {data.fee_amount}
                  </span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.exchangeRate')}</span>
                  <span className="payout_drawer__summary__value">{data.exchange_rate || '-'}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.purpose')}</span>
                  <span className="payout_drawer__summary__value">{data.purpose || '-'}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.description')}</span>
                  <span className="payout_drawer__summary__value">{data.description || '-'}</span>
                </div>
              </div>
            )}
          </div>

          <div className="payout_drawer__section">
            <div
              className="payout_drawer__section__header"
              onClick={() => setRecipientDetailOpen(!recipientDetailOpen)}
            >
              <span>{l('payouts.drawer.recipientDetail')}</span>
              <KeyboardArrowUpIcon
                className={`payout_drawer__section__arrow ${!recipientDetailOpen ? 'payout_drawer__section__arrow--collapsed' : ''}`}
              />
            </div>
            {recipientDetailOpen && (
              <div className="payout_drawer__section__body">
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.accountType')}</span>
                  <span className="payout_drawer__summary__value">{data.account_type || '-'}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.accountHolder')}</span>
                  <span className="payout_drawer__summary__value">
                    {data.beneficiary_first_name} {data.beneficiary_last_name}
                  </span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.accountNumber')}</span>
                  <span className="payout_drawer__summary__value">{data.bank_account || '-'}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.beneficiaryDocument')}</span>
                  <span className="payout_drawer__summary__value">{data.beneficiary_document || '-'}</span>
                </div>
                <div className="payout_drawer__summary__row">
                  <span className="payout_drawer__summary__label">{l('payouts.drawer.email')}</span>
                  <span className="payout_drawer__summary__value">{data.beneficiary_email || '-'}</span>
                </div>
              </div>
            )}
          </div>

          <div className="payout_drawer__actions">
            <Button
              textTransformNone
              startIcon={<FileDownload />}
              size="small"
              variant="outlined"
              onClick={() => exportPayoutDetail(Number(data.payout_id))}
            >
              {l('payouts.drawer.downloadReceipt')}
            </Button>
          </div>
        </div>
      </Card>
    </div>,
    window.document.body
  );
};

export default PayoutDrawer;
